// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

/**
 * B22a 历史回放投影：用 session-events 的轮次快照回填历史消息 UI 载荷。
 *
 * 背景（E2E 审计 2026-09-17）：checkpoint 的 `state.ui` 是单槽（每轮覆盖），
 * 历史回放时除末轮外 ui_blocks/rag_status 全裸（G7-I 缺口）；后端两次尝试
 * （finalize 给消息挂 additional_kwargs）经栈内实证**均不落 GET 读取的
 * checkpoint 路径**（facade 图 state patch 与读取路径断层）。而
 * `ui_state_persisted` 事件的 payload.values 每轮携带**完整快照**
 * （ui.ui_blocks / artifacts_runtime.visualization_state / retrieval.rag_status
 * 等），是回放投影的天然数据源。
 *
 * 实现：读事件流 → 以"该快照里最后一条 assistant 消息的 id"为键建映射 →
 * 历史加载后按 id 回填。匹配不上的轮次保持原状（单调递进，不破坏现状）。
 */

import type { Message, SessionEvent, SessionEventListResponse } from '@/types';
import { api } from '@api/client';

export interface UiSnapshotPatch {
  ui_blocks?: Message['ui_blocks'];
  action_revision?: number;
  visualization_state?: Message['visualization_state'];
  ui_generation_mode?: Message['ui_generation_mode'];
  rag_status?: string | null;
  /** INFRA-04：本轮联网检索执行状态（快照回放）。 */
  web_search_status?: string | null;
  retrieval_mode?: string | null;
  rag_sources?: Message['sources'];
}

const UI_STATE_EVENT_TYPE = 'ui_state_persisted';
/** 覆盖历史轮次所需的事件量级；单线程事件总数远小于该值（分页上限内）。 */
const SNAPSHOT_EVENT_LIMIT = 200;

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** 从快照 messages 里取最后一条 assistant 消息的 id（回填锚点）。 */
function lastAssistantMessageId(valuesMessages: unknown): string | null {
  if (!Array.isArray(valuesMessages)) return null;
  for (let index = valuesMessages.length - 1; index >= 0; index -= 1) {
    const raw = valuesMessages[index];
    const item = Array.isArray(raw) ? raw[0] : raw;
    const record = asRecord(item);
    if (!record) continue;
    const data = asRecord(record.data) ?? record;
    const type = String(record.type ?? data.type ?? '').toLowerCase();
    const role = String(data.role ?? '').toLowerCase();
    if (type !== 'ai' && role !== 'assistant') continue;
    const id = String(data.id ?? record.id ?? '').trim();
    if (id) return id;
  }
  return null;
}

/** 把一条 ui_state_persisted 快照转成消息级补丁（无有效内容时返回 null）。 */
export function snapshotPatchFromEventPayload(payload: unknown): {
  messageId: string | null;
  patch: UiSnapshotPatch;
} | null {
  const payloadRecord = asRecord(payload);
  const values = asRecord(payloadRecord?.values);
  if (!values) return null;

  const ui = asRecord(values.ui);
  const artifactsRuntime = asRecord(values.artifacts_runtime);
  const retrieval = asRecord(values.retrieval);

  const patch: UiSnapshotPatch = {};
  const uiBlocks = ui?.ui_blocks;
  if (Array.isArray(uiBlocks) && uiBlocks.length > 0) {
    patch.ui_blocks = uiBlocks as Message['ui_blocks'];
  }
  const visualizationState = artifactsRuntime?.visualization_state;
  if (visualizationState && typeof visualizationState === 'object') {
    patch.visualization_state = visualizationState as Message['visualization_state'];
  }
  if (retrieval) {
    const ragStatus = retrieval.rag_status;
    if (typeof ragStatus === 'string' && ragStatus.trim()) {
      patch.rag_status = ragStatus;
    }
    // INFRA-04：快照回放同源读取联网检索执行状态。
    const webSearchStatus = retrieval.web_search_status;
    if (typeof webSearchStatus === 'string' && webSearchStatus.trim()) {
      patch.web_search_status = webSearchStatus;
    }
    const retrievalMode = retrieval.retrieval_mode;
    if (typeof retrievalMode === 'string' && retrievalMode.trim()) {
      patch.retrieval_mode = retrievalMode;
    }
    const ragSources = retrieval.rag_sources;
    if (Array.isArray(ragSources) && ragSources.length > 0) {
      patch.rag_sources = ragSources as Message['sources'];
    }
  }

  // ui_generation_mode 是附属标记：仅当快照已有实质载荷时附带，
  // 避免"空轮次"（mode=none、无任何 UI 载荷）也建映射条目。
  const uiGenerationMode = ui?.ui_generation_mode;
  if (
    Object.keys(patch).length > 0 &&
    (uiGenerationMode === 'tool' || uiGenerationMode === 'none')
  ) {
    patch.ui_generation_mode = uiGenerationMode;
  }

  if (Object.keys(patch).length === 0) {
    return null;
  }
  if (typeof ui?.ui_action_revision === 'number' && Number.isInteger(ui.ui_action_revision)) {
    patch.action_revision = ui.ui_action_revision;
  }
  return { messageId: lastAssistantMessageId(values.messages), patch };
}

/** 事件列表 → assistant 消息 id → UI 补丁（按时间序，后到者覆盖先到者）。 */
export function buildUiSnapshotMap(events: unknown[]): Map<string, UiSnapshotPatch> {
  const map = new Map<string, UiSnapshotPatch>();
  for (const event of events) {
    const record = asRecord(event);
    if (!record) continue;
    if (String(record.event_type ?? '') !== UI_STATE_EVENT_TYPE) continue;
    const parsed = snapshotPatchFromEventPayload(record.payload);
    if (!parsed || !parsed.messageId) continue;
    map.set(parsed.messageId, parsed.patch);
  }
  return map;
}

/**
 * 拉取线程的轮次快照映射。失败一律返回空 Map（回放增强是渐进能力，
 * 不得阻断历史加载主路径）。
 */
export async function fetchUiSnapshotMap(threadId: string): Promise<Map<string, UiSnapshotPatch>> {
  const normalized = String(threadId || '').trim();
  if (!normalized) return new Map();
  try {
    const { data } = await api.getThreadSessionEvents(normalized, {
      limit: SNAPSHOT_EVENT_LIMIT,
      event_type: UI_STATE_EVENT_TYPE,
    });
    const events = Array.isArray((data as SessionEventListResponse | undefined)?.events)
      ? ((data as SessionEventListResponse).events as SessionEvent[])
      : [];
    return buildUiSnapshotMap(events);
  } catch {
    return new Map();
  }
}

/** 按 id 把快照补丁合并进历史消息（不改动未匹配的消息）。 */
export function applyUiSnapshotsToMessages(
  messages: Message[],
  snapshots: Map<string, UiSnapshotPatch>
): Message[] {
  if (!messages.length || snapshots.size === 0) return messages;
  return messages.map((message) => {
    if (message.role !== 'assistant') return message;
    const patch = snapshots.get(message.id);
    if (!patch) return message;
    const hasNewerCheckpointUi =
      typeof message.action_revision === 'number' &&
      message.action_revision > (patch.action_revision ?? 0) &&
      Array.isArray(message.ui_blocks) && message.ui_blocks.length > 0;
    return {
      ...message,
      ui_blocks: hasNewerCheckpointUi ? message.ui_blocks : patch.ui_blocks ?? message.ui_blocks,
      visualization_state: hasNewerCheckpointUi
        ? message.visualization_state
        : patch.visualization_state ?? message.visualization_state,
      ui_generation_mode: patch.ui_generation_mode ?? message.ui_generation_mode,
      action_revision: Math.max(message.action_revision ?? 0, patch.action_revision ?? 0),
      rag_status: patch.rag_status ?? message.rag_status,
      retrieval_mode: patch.retrieval_mode ?? message.retrieval_mode,
      sources: patch.rag_sources ?? message.sources,
    };
  });
}


/**
 * B22b-B（2026-09-17）：从历史 checkpoint 的 `ui.clarification_cards` 提取
 * 已提交澄清卡 → 消息级折叠卡 ui_block。
 *
 * 桥节点在 resume 时把 Q&A 写入 `ui.clarification_cards`（append 语义，
 * checkpoint 持久化）；但 resume 链（root_graph）不经过 facade finalize，
 * 不产生 ui_state_persisted 快照——故回放数据直接从历史 checkpoint 读取
 * （API 实测该字段长期在态，属既有数据未渲染）。
 */
export function clarificationCardBlocksFromCheckpoint(
  payload: unknown
): NonNullable<Message['ui_blocks']> | undefined {
  const record = asRecord(payload);
  const checkpoint = asRecord(record?.checkpoint);
  const channelValues = asRecord(checkpoint?.channel_values);
  const ui = asRecord(channelValues?.ui);
  const cards = ui?.clarification_cards;
  if (!Array.isArray(cards) || cards.length === 0) return undefined;
  const blocks = cards
    .filter((card) => asRecord(card))
    .map((card, index) => {
      const cardRecord = card as Record<string, unknown>;
      // id 与后端 finalize 生成器（copilot_nodes.py `clarify-card-{int}`）同构：
      // 原 String(submitted_at) 带小数与消息内已挂块 id 不相等，去重失效。
      return {
        id: `clarify-card-${String(Math.trunc(Number(cardRecord.submitted_at ?? index)) || index)}`,
        type: 'clarification_card' as const,
        title: '澄清问答',
        payload: {
          card: cardRecord as unknown as Extract<
            Message['ui_blocks'],
            { type: 'clarification_card' }[]
          >[number]['payload']['card'],
        },
      };
    });
  return blocks.length > 0 ? blocks : undefined;
}

/** 把澄清卡块挂到最后一条 assistant 消息（无 assistant 时不动）。 */
export function attachClarificationCards(
  messages: Message[],
  blocks: Message['ui_blocks']
): Message[] {
  if (!blocks || blocks.length === 0) return messages;
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index].role !== 'assistant') continue;
    // 终验 2026-09-17：历史 AI 消息的 ui_blocks 可能已含同 id 卡（快照通道
    // 与 checkpoint 通道都以 `clarify-card-<submitted_at>` 为 id），双重挂载
    // 会把每张已提交卡渲染两遍——按 id 去重后只补缺口。
    const existingIds = new Set(
      (messages[index].ui_blocks ?? []).map((block) => block.id)
    );
    const deduped = blocks.filter((block) => !existingIds.has(block.id));
    if (deduped.length === 0) return messages;
    const next = [...messages];
    next[index] = {
      ...messages[index],
      ui_blocks: [...(messages[index].ui_blocks ?? []), ...deduped],
    };
    return next;
  }
  return messages;
}
